/**
 * Edit one model entry's DEFAULT VALUES.
 *
 * WHAT THIS IS NOT. It is not `ParamSpecEditor`, which edits the capability SPEC
 * — the bounds, the ui hint, whether the param exists at all. Before the split those
 * were the same write through the same route and discovery owned the blob, so a
 * catalog Sync overwrote `supported_params` wholesale and silently erased a
 * tuned default with it. The split is what makes a default a durable, user-owned
 * preference rather than something a sync can revert.
 *
 * THREE PROPERTIES WORTH READING THE CODE FOR:
 *
 *  1. IT SHOWS WHICH TIER WON. `defaults_source` says, per key, whether the value
 *     in force was set on this entry, inherited from the provider, or is the
 *     built-in. Without it, a user editing an entry default cannot see what they
 *     are leaving — the number is identical either way.
 *
 *  2. IT WRITES BACK ONLY THE ENTRY TIER. `defaults` is the MERGED result, so
 *     echoing it wholesale would pin every inherited provider value onto this
 *     entry — the same "seeding" defect sparse pins removed from the agent editor, one
 *     tier up. Only keys whose source is `model` are part of the entry's blob,
 *     plus whatever the user changes here. Clearing sends `null`, which drops
 *     back to the provider tier.
 *
 *  3. IT ASKS THE BLAST RADIUS FIRST. `listDefaultOverrides` names the agents
 *     that PIN a param being changed, because those are precisely the ones the
 *     change will NOT reach. Every other agent picks it up on its next turn. The
 *     count is shown before the write, while the user can still decide,
 *     and it never blocks.
 *
 * The fingerprint is echoed on the write. A stale one returns 409
 * `catalog_schema_stale`, which is rendered as "reload", not as a field error —
 * see `AgentParamNotices.SchemaStaleNotice` for why those are different remedies.
 */
import { useEffect, useMemo, useState } from 'react';
import { Button } from '@localagent/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@localagent/ui/components/dialog';
import { isConflict } from '@localagent/sdk';
import type { ParamSchema, ProviderType, SupportedParamSpec } from '@localagent/sdk';
import { useParamSchema } from '../../hooks/useAgentDefinitions';
import { useDefaultOverrides, useSetModelParamDefaults } from '../../hooks/useProviderManagement';
import { SchemaStaleNotice } from '../agents/AgentParamNotices';

/** Human label for which tier a resolved default came from. */
const TIER_LABEL: Record<string, string> = {
  model: 'Set on this entry',
  provider: 'From provider',
  builtin: 'Built-in',
};

/**
 * The keys this ENTRY owns, reconstructed from the resolved schema.
 *
 * There is no read endpoint for an entry's raw `default_params` blob, so this is
 * derived: `defaults_source[k] === 'model'` is exactly "this entry set it". The
 * derivation matters because the write replaces the blob wholesale — sending the
 * merged `defaults` instead would silently promote every provider-tier value
 * into this entry, after which a provider-level correction would stop reaching
 * it.
 */
export function entryTierDefaults(schema: ParamSchema): Record<string, unknown> {
  const own: Record<string, unknown> = {};
  for (const [key, source] of Object.entries(schema.defaults_source)) {
    if (source === 'model') own[key] = schema.defaults[key];
  }
  return own;
}

/** Parse a typed field back into the scalar the spec declares. */
function parseValue(text: string, spec: SupportedParamSpec): number | string {
  if (spec.type === 'integer') return parseInt(text, 10);
  if (spec.type === 'numeric') return parseFloat(text);
  return text;
}

export interface ModelDefaultsDialogProps {
  providerType: ProviderType;
  modelName: string;
  open: boolean;
  onClose: () => void;
}

export function ModelDefaultsDialog({
  providerType,
  modelName,
  open,
  onClose,
}: ModelDefaultsDialogProps) {
  const schemaQuery = useParamSchema(open ? providerType : undefined, open ? modelName : undefined);
  const schema = schemaQuery.data;
  const setDefaults = useSetModelParamDefaults();

  // Per-key draft text. A key absent from the draft is untouched; a key present
  // with '' is a deliberate CLEAR (sent as null → drops to the provider tier).
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState('');
  const [schemaStale, setSchemaStale] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDraft({});
    setSaveError('');
    setSchemaStale(false);
  }, [open, providerType, modelName]);

  const changedKeys = useMemo(() => Object.keys(draft), [draft]);

  // Asked BEFORE the write, and scoped to the keys actually changing.
  const impact = useDefaultOverrides(
    open ? providerType : undefined,
    changedKeys,
    modelName,
  );

  const handleSave = () => {
    if (!schema) return;
    setSaveError('');
    setSchemaStale(false);

    const next = entryTierDefaults(schema);
    for (const [key, text] of Object.entries(draft)) {
      const spec = schema.params[key];
      if (!spec) continue;
      if (text.trim() === '') {
        // null CLEARS back to the provider tier — distinct from
        // omitting the key, which would simply leave this entry's value in place.
        next[key] = null;
        continue;
      }
      const parsed = parseValue(text, spec);
      if (typeof parsed === 'number' && Number.isNaN(parsed)) {
        setSaveError(`${spec.label}: enter a number`);
        return;
      }
      next[key] = parsed;
    }

    setDefaults.mutate(
      {
        providerType,
        modelName,
        defaults: next,
        // Mandatory, not best-effort. Echoing the schema this
        // dialog rendered from is what lets the server refuse a write aimed at a
        // schema that has since moved.
        fingerprint: schema.fingerprint,
      },
      {
        onSuccess: () => onClose(),
        onError: error => {
          if (isConflict(error)) {
            setSchemaStale(true);
            return;
          }
          // The server's words, verbatim — an out-of-bounds default is refused
          // with the param and the bound named, and restating that in
          // the client is how the two come to disagree.
          setSaveError(error.message);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={value => { if (!value) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="model-defaults-dialog">
        <DialogHeader>
          <DialogTitle>Default values — {modelName}</DialogTitle>
          <DialogDescription>
            Defaults are what agents use when they do not override a parameter. Changing one
            reaches every agent that has not pinned it, on its next turn.
          </DialogDescription>
        </DialogHeader>

        {schemaStale && (
          <SchemaStaleNotice
            onReload={() => {
              setSchemaStale(false);
              void schemaQuery.refetch();
            }}
          />
        )}

        {schemaQuery.isError && (
          <p data-testid="model-defaults-schema-error" className="text-body text-destructive">
            {schemaQuery.error.message}
          </p>
        )}

        {schema && (
          <ModelDefaultsRows
            schema={schema}
            draft={draft}
            onDraftChange={(key, text) => setDraft(prev => ({ ...prev, [key]: text }))}
            disabled={setDefaults.isPending}
          />
        )}

        {changedKeys.length > 0 && impact.data && (
          <DefaultsImpactPanel
            count={impact.data.count}
            personaNames={impact.data.personas.map(p => p.persona_name)}
            defaultsVersion={impact.data.defaults_version}
          />
        )}

        {saveError && (
          <p data-testid="model-defaults-error" className="text-body text-destructive">
            {saveError}
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={setDefaults.isPending}>
            Cancel
          </Button>
          <Button
            data-testid="model-defaults-save"
            onClick={handleSave}
            disabled={!schema || changedKeys.length === 0 || setDefaults.isPending}
          >
            {setDefaults.isPending ? 'Saving…' : 'Save defaults'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The rows. Split out and exported so the functional story tier can render them
 * from a `ParamSchema` fixture without a live backend — the dialog shell needs
 * the SDK, the rows are pure presentation.
 */
export function ModelDefaultsRows({
  schema,
  draft,
  onDraftChange,
  disabled,
}: {
  schema: ParamSchema;
  draft: Record<string, string>;
  onDraftChange: (key: string, text: string) => void;
  disabled: boolean;
}) {
  const sorted = Object.entries(schema.params).sort(
    ([, a], [, b]) => (a.sort_order ?? 0) - (b.sort_order ?? 0),
  );
  return (
    <div className="space-y-3">
      {sorted.map(([key, spec]) => {
        const source = schema.defaults_source[key];
        const inForce = schema.defaults[key];
        const text = key in draft ? draft[key] : inForce != null ? String(inForce) : '';
        return (
          <div key={key} data-testid={`model-default-${key}`} data-default-source={source ?? 'none'}>
            <div className="mb-1 flex items-center gap-2">
              <label className="text-label font-medium">{spec.label}</label>
              {/* WHICH TIER THE CURRENT VALUE CAME FROM. Without it the
                  user cannot see what they are about to override — the number
                  reads the same whichever tier produced it. */}
              {source && (
                <span
                  data-testid={`model-default-${key}-source`}
                  className="rounded-full bg-muted px-2 py-0.5 text-micro font-medium uppercase tracking-wide text-muted-foreground"
                >
                  {TIER_LABEL[source] ?? source}
                </span>
              )}
              {source !== 'model' && inForce != null && (
                <span className="text-micro text-muted-foreground">
                  currently <strong data-testid={`model-default-${key}-inherited-value`}>{String(inForce)}</strong>
                </span>
              )}
            </div>
            <input
              type="text"
              data-testid={`model-default-${key}-input`}
              value={text}
              onChange={e => onDraftChange(key, e.target.value)}
              disabled={disabled}
              className="w-full rounded-md bg-muted px-3 py-2 text-body focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
              placeholder="Empty clears back to the provider default"
            />
          </div>
        );
      })}
    </div>
  );
}

/**
 * The change-impact answer, shown BEFORE the write and blocking nothing.
 *
 * `count` is the number of agents that will NOT receive the change. Stating it
 * the other way round — "N agents affected" — would be the wrong number and the
 * wrong lesson: under sparse pins every agent that has not pinned the param
 * picks the new value up automatically, and the ones worth naming are the
 * exceptions.
 */
export function DefaultsImpactPanel({
  count,
  personaNames,
  defaultsVersion,
}: {
  count: number;
  personaNames: string[];
  defaultsVersion: number;
}) {
  return (
    <div
      data-testid="defaults-impact-panel"
      data-impact-count={String(count)}
      data-defaults-version={String(defaultsVersion)}
      className="rounded-md bg-muted p-3 text-caption"
    >
      {count === 0 ? (
        <span>
          No agent pins these parameters — every agent on this model picks the new value up on
          its next turn.
        </span>
      ) : (
        <span>
          <strong>
            {count} agent{count === 1 ? '' : 's'} pin
            {count === 1 ? 's' : ''} these parameters
          </strong>{' '}
          and will keep their own value:{' '}
          <span data-testid="defaults-impact-personas">{personaNames.join(', ')}</span>. Every
          other agent picks the new value up on its next turn.
        </span>
      )}
    </div>
  );
}
